import { AssistanceEditorControlEnabledConditions, AssistanceEditorForm } from './AssistanceEditorForm'
import { EditorAssistance } from '../../../models/EditorAssistance'
import { Client } from '../../../models/client'
import { firstValueFrom } from 'rxjs'
import { FeatureFlags } from '../../../shared/featureFlags'
import { addMinutes, differenceInMinutes } from 'date-fns'
import { AssistanceStatus, AssistanceStatusName } from '../../../models/assistanceStatus'
import type { AssistanceEditorComponent } from './assistance-editor.component'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { FormControl } from '@angular/forms'
import { DateSegment } from '../../../models/DateTime/DateSegment'
import { DateTime } from '../../../models/DateTime/DateTime'
import equal from 'fast-deep-equal/es6'
import { SimpleStash } from '../../stash/SimpleStash'

export class EditorAssistanceFormValueChangeHandler {
  private TAG = 'EditorAssistanceFormValueChangeHandler'

  constructor(
    private ctx: AssistanceEditorComponent,
    private statuses: AssistanceStatus[],
  ) {}

  private get c() {
    return this.ctx.form.controls
  }

  setValueChangesHandlers() {
    const c = this.c

    c.plannedFrom.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handlePlannedFromChange(value))
    c.clientId.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handleClientIdChange(value))
    c.rrule.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.ctx.unsubscribe$))
      .subscribe((value) => this.handleRruleChange(value))

    this.ctx.form.valueChanges.pipe(takeUntil(this.ctx.unsubscribe$)).subscribe((formValue) => {
      this.handleFormControlsDisabledStates()
    })
  }

  handleInitial() {
    this.handleFormControlsDisabledStates()
  }

  private handlePlannedFromChange(plannedFromValue: DateTime) {
    // run only when both date and time are set
    if (plannedFromValue.isComplete) {
      const plannedFromStash: SimpleStash<DateTime> = this.ctx.formStashes
        .get(+this.ctx.assistance.id)
        .get('plannedFrom')
      plannedFromStash.stash(plannedFromValue)

      if (FeatureFlags.assistanceEditor_adaptiveTimeTo) {
        const plannedToValue = this.ctx.form.controls.plannedTo.value

        // 1. If plannedTo is complete, adapt time by delta between current plannedFrom and old plannedFrom
        if (plannedToValue.isComplete) {
          this.adaptPlannedToByDelta(plannedFromStash)
        }

        // 2. If plannedTo is empty, autofill both date and time from plannedFrom + 1 hour
        if (plannedToValue.isEmpty) {
          this.autoFillPlannedTo(plannedFromValue)
        }
      }
    }
  }

  private adaptPlannedToByDelta(plannedFromStash: SimpleStash<DateTime>) {
    if (
      plannedFromStash.isChanged((a, b) => equal(a.time, b.time)) &&
      plannedFromStash.current &&
      plannedFromStash.previous
    ) {
      // Adapt plannedTo time when plannedFrom time changes
      const deltaMinutes = differenceInMinutes(plannedFromStash.current?.toDate(), plannedFromStash.previous?.toDate())
      this.ctx.form.controls.plannedTo.markAsDirty()
      this.ctx.form.controls.plannedTo.patchValue(
        DateTime.fromDate(addMinutes(this.ctx.form.getRawValue().plannedTo?.toDate(), deltaMinutes)),
        { emitEvent: true },
      )
    }
  }

  private autoFillPlannedTo(plannedFromValue: DateTime) {
    const plannedFromDate = plannedFromValue.toDate()
    const plannedToDate = addMinutes(plannedFromDate, 60)
    const plannedToDateTime = DateTime.fromDate(plannedToDate)
    const plannedToControl = this.ctx.form.controls.plannedTo

    plannedToControl.patchValue(plannedToDateTime)
  }

  private async handleClientIdChange(clientId: number) {
    console.log(`${this.TAG}#handleClientIdChange ${clientId}`)
    if (!clientId) return

    const client = await firstValueFrom(this.ctx.clientsService.getById(clientId))
    this.loadDefaultClientValuesIntoForm(client)
  }

  private loadDefaultClientValuesIntoForm(client: Client) {
    console.log(this.TAG, 'loadDefaultClientValues', client)

    this.ctx.form.controls.keyCoordinatorId.markAsDirty()
    this.ctx.form.controls.preferredAssistantGenderId.markAsDirty()
    this.ctx.form.controls.strongAssistantNeeded.markAsDirty()
    this.ctx.form.controls.advancedAssistantNeeded.markAsDirty()

    this.ctx.form.patchValue(
      {
        keyCoordinatorId: client.keyCoordinatorId,
        preferredAssistantGenderId: client.preferredGenderId,
        strongAssistantNeeded: client.needsStrongAssistant,
        advancedAssistantNeeded: client.needsAdvancedAssistant,
      },
      { emitEvent: true },
    )
  }

  private handleFormControlsDisabledStates(
    form: AssistanceEditorForm = this.ctx.form,
    assistance: EditorAssistance = this.ctx.assistance,
    statuses = this.statuses,
  ) {
    function getEnablingRules() {
      const c = form.controls

      const clientId = c.clientId.value
      const assistantAssignments = c.assistantAssignments.value
      const statusId = c.statusId.value
      const status = statuses.find((s) => s.id === statusId)
      const isBilled = assistance.isBilled

      const rules: AssistanceEditorControlEnabledConditions = {
        clientId: () => (!assistantAssignments || !assistantAssignments.length) && !isBilled,
        urid: () => !!(clientId && !isBilled),
        plannedFrom: () =>
          !!(
            clientId &&
            !isBilled &&
            status.isOneOf([AssistanceStatusName.PLANNED, AssistanceStatusName.HAS_ASSISTANT])
          ),
        plannedTo: () =>
          !!(
            clientId &&
            !isBilled &&
            status.isOneOf([AssistanceStatusName.PLANNED, AssistanceStatusName.HAS_ASSISTANT])
          ),
        realizedFrom: () =>
          !!(
            clientId &&
            status.isAtLeast(AssistanceStatusName.CONFIRMED) &&
            assistantAssignments &&
            assistantAssignments.length &&
            !isBilled
          ),
        realizedTo: () => !!c.realizedFrom.enabled,
        addressStart: () => !!(clientId && !isBilled),
        addressEnd: () => !!(clientId && !isBilled),
        statusId: () => !!(clientId && !isBilled),
        keyCoordinatorId: () => !!(clientId && !isBilled),
        assistantAssignments: () => !!(clientId && !isBilled),
        isSentToClient: () => !!(clientId && !isBilled),
        coordinatorNote: () => !!(clientId && !isBilled),
        shortDescription: () => !!(clientId && !isBilled),
        detailedDescription: () => !!(clientId && !isBilled),
        isPaidInternally: () => !!(clientId && !isBilled),
        isNotToBePrimarilyAssigned: () => !!(clientId && !isBilled),
        isLateOrder: () => !!(clientId && !isBilled),
        preferredAssistantGenderId: () => !!(clientId && !isBilled),
        strongAssistantNeeded: () => !!(clientId && !isBilled),
        advancedAssistantNeeded: () => !!(clientId && !isBilled),
        isSleepover: () => !!(clientId && !isBilled),
        nonClientWork: () => !!(clientId && !isBilled),
        isShowInExchange: () => !!(clientId && !isBilled),
        journalEntries: () => !!clientId,
        rrule: () => !!(clientId && !isBilled),
      }

      return rules
    }

    const currentRules = getEnablingRules()

    for (const controlName in form.controls) {
      const control: FormControl = form.controls[controlName]
      if (currentRules[controlName]) {
        const targetState = currentRules[controlName]()
        console.log('targetState', targetState)
        if (targetState && control.disabled) {
          control.enable({ emitEvent: false })
        } else if (!targetState && control.enabled) {
          control.disable({ emitEvent: false })
        }
      }
    }

    this.ctx.recurrenceEditorShowManager.setReason('assistantMissing', !this.ctx.assistance.hasAssignments())
    this.ctx.recurrenceEditorShowManager.setReason('disabledPlannedTimes', this.c.plannedFrom.disabled)
  }

  private handleRruleChange(rrule: string) {
    if (rrule) this.c.isShowInExchange.patchValue(false)
  }

  // TODO: Incorporate!!
  // this.form.get('assistantAssignments').valueChanges
  //     .pipe(takeUntil(this.unsubscribe$))
  //     .subscribe((assignmentList: AssistantAssignmentList) => {
  //
  //       // Allow adding assistant only for currentOccurrence
  //       if (this.recurrenceEditingMode !== RecurrenceEditingMode.none) this.changeRecurrenceEditingMode(RecurrenceEditingMode.currentOccurrence)
  //
  //     })
}
